export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect()

  return x >= left && x <= right && y >= top && y <= bottom
}

export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(event.clientX, event.clientY, element)
  }

  return !element.contains(event.target)
}

export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback
    : setTimeout

export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0)
  })
}

export function debounce(fn, wait) {
  let timeout

  function debounced(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, args), wait)
  }

  debounced.cancel = () => {
    clearTimeout(timeout)
  }

  return debounced
}

export function throttle(fn, delay) {
  let lastCall = 0

  function throttled(...args) {
    const now = performance.now()
    if (now - lastCall >= delay) {
      lastCall = now
      fn.apply(this, args)
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now()
  }

  return throttled
}

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')

export function useReduced() {
  return reducedMotion.matches
}

export function getIOSVersion() {
  const { userAgent } = navigator
  const isIOS = /(iPhone|iPad)/i.test(userAgent)

  if (!isIOS) {
    return null
  }

  const version = userAgent.match(/OS ([\d_]+)/)?.[1]
  const [major, minor] = version?.split('_') || []
  if (!(version && major)) {
    return null
  }

  return {
    fullString: version.replace('_', '.'),
    major: Number.parseInt(major, 10),
    minor: minor ? Number.parseInt(minor, 10) : 0
  }
}

export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback)
  } else {
    callback()
  }
}

export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true }
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options)
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished)
    }

    return acc
  }, [])

  return Promise.allSettled(animationPromises).then(callback)
}

export function parseItemCountFromSectionsHTML(sections) {
  if (!sections || typeof sections !== 'object') {
    return null
  }
  for (const sectionId in sections) {
    if (!Object.hasOwn(sections, sectionId)) continue
    const html = sections[sectionId]
    if (typeof html !== 'string') continue
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const node = doc.querySelector('[ref="cartItemCount"]')
    if (!node) continue
    const count = Number.parseInt(node.textContent?.trim() ?? '', 10)
    if (!Number.isNaN(count)) return count
  }
  return null
}

export function fetchConfig(type = 'json', config = {}) {
  const headers = {
    Accept: `application/${type}`,
    'Content-Type': 'application/json',
    ...config.headers
  }

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest'
    delete headers['Content-Type']
  }

  return {
    body: config.body,
    headers: headers,
    method: 'POST'
  }
}
